import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, User } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminUsers } from '@/hooks/useAdminUsers';

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useAdminUsers();

  useEffect(() => {
    document.title = 'Admin Users | Admin Dashboard';
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, []);

  useEffect(() => {
    if (isError && error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load admin users');
    }
  }, [isError, error]);

  const roleBadgeClass = (role: string) => {
    return role === 'super_admin'
      ? 'bg-purple-50 text-purple-700 border-purple-200'
      : 'bg-sky-50 text-sky-700 border-sky-200';
  };

  const roleLabel = (role: string) => {
    return role === 'super_admin' ? 'Super Admin' : 'Creator';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div>
          <h1 className="text-pine-display font-display font-semibold text-pine-ink">Admin Users</h1>
          <p className="text-pine-body text-pine-ink-soft mt-1">
            Manage admin users and profile assignments
          </p>
        </div>
      </motion.div>

      {/* Error State */}
      {isError && (
        <div className="admin-card p-6 border border-pine-danger/20 bg-pine-danger/5">
          <p className="text-pine-danger">Failed to load admin users. Please try again.</p>
        </div>
      )}

      {/* Table */}
      {!isError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="admin-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-pine-line">
                  <th className="text-left px-6 py-4 text-sm font-medium text-pine-ink-soft">
                    Name
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-pine-ink-soft">
                    Email
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-pine-ink-soft">
                    Role
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-pine-ink-soft">
                    Assigned Profile
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-pine-ink-soft">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <Loader2 className="w-6 h-6 animate-spin text-pine-pine mx-auto" />
                    </td>
                  </tr>
                ) : data?.items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-pine-ink-soft">
                      No admin users found
                    </td>
                  </tr>
                ) : (
                  data?.items.map((user) => (
                    <tr
                      key={user.id}
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                      className="border-b border-pine-line hover:bg-pine-hover-item cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-pine-l2 flex items-center justify-center border border-pine-line">
                            <User className="w-4 h-4 text-pine-pine" />
                          </div>
                          <span className="text-sm font-medium text-pine-ink">
                            {user.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-pine-ink-soft">{user.email}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border ${roleBadgeClass(user.role)}`}
                        >
                          {roleLabel(user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-pine-ink-soft">
                        {user.expertProfile ? user.expertProfile.name : '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-pine-ink-soft">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </div>
  );
}
